import { Link } from "react-router-dom";
import { ArrowRight, Activity, Target, FileBarChart2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1512719994953-eabf50895df7?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const DashboardPreview = () => (
  <div className="floaty card-surface lime-glow p-5 w-full max-w-[400px] backdrop-blur-xl bg-[#111511]/85" data-testid="hero-dashboard-preview">
    <div className="flex items-center justify-between mb-4">
      <p className="font-display text-lg font-bold uppercase">Session Report</p>
      <span className="font-mono text-[10px] px-2 py-1 rounded-full border border-[#B7FF3C]/40 text-[#B7FF3C]">ILLUSTRATIVE</span>
    </div>
    <div className="space-y-3">
      {[["Technique observations", "Front-foot drive · head position"], ["Strengths", "Balanced stance at release"], ["Improvement areas", "Back-lift consistency"]].map(([k, v]) => (
        <div key={k} className="rounded-lg border border-[#1E281E] bg-[#080A08]/70 p-3">
          <p className="eyebrow !text-[10px] !tracking-[0.15em]">{k}</p>
          <p className="text-sm text-[#DDE3DD] mt-1">{v}</p>
        </div>
      ))}
      <div className="rounded-lg border border-[#1E281E] bg-[#080A08]/70 p-3">
        <p className="eyebrow !text-[10px] !tracking-[0.15em] mb-2">Performance insights</p>
        <div className="flex items-end gap-1.5 h-14">
          {[40, 62, 48, 75, 58, 82, 66].map((h, i) => (
            <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-[#B7FF3C]/30 to-[#B7FF3C]" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
    </div>
    <p className="text-[10px] text-[#6B776B] mt-3">Sample layout only. Not real analysis data.</p>
  </div>
);

export const Hero = () => (
  <section className="relative overflow-hidden noise min-h-[calc(100vh-72px)] flex items-center" data-testid="hero-section">
    <img src={HERO_IMG} alt="Cricketer under stadium lights" className="absolute inset-0 w-full h-full object-cover opacity-35" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#080A08] via-[#080A08]/85 to-[#080A08]/40" />
    <div className="absolute inset-0 grid-bg opacity-60" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-[1.25fr_1fr] gap-14 items-center w-full">
      <div>
        <span className="rise inline-flex items-center gap-2 eyebrow px-3 py-1.5 rounded-full border border-[#B7FF3C]/30 bg-[#B7FF3C]/5" data-testid="hero-badge">
          <span className="w-1.5 h-1.5 rounded-full bg-[#B7FF3C] animate-pulse" /> The Future of Cricket Coaching
        </span>

        <h1 className="rise mt-7 text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.92]" style={{ animationDelay: ".08s" }}>
          Analyze.<br />Improve.<br /><span className="text-[#B7FF3C]">Play Better.</span>
        </h1>
        <p className="rise mt-6 font-display text-xl md:text-2xl uppercase tracking-wide text-[#DDE3DD]" style={{ animationDelay: ".16s" }}>Your Cricket Game. Under the Microscope.</p>
        <p className="rise mt-4 max-w-xl text-base text-[#A3ADA3] leading-relaxed" style={{ animationDelay: ".22s" }}>
          Join CricAnalyze and get ready to turn your batting and bowling videos into meaningful performance insights, detailed reports, and smarter training opportunities.
        </p>
        <div className="rise mt-9 flex flex-wrap gap-3" style={{ animationDelay: ".3s" }}>
          <Link to="/join" data-testid="hero-join-waitlist-btn" className="group px-7 py-3.5 rounded-full bg-[#B7FF3C] text-[#080A08] font-bold flex items-center gap-2 hover:bg-[#c9ff6a] transition-colors">
            Join the Waitlist <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <a href="#how-it-works" data-testid="hero-explore-btn" className="px-7 py-3.5 rounded-full border border-[#2A362A] text-white hover:border-[#B7FF3C]/60 transition-colors">Explore How It Works</a>
        </div>
        <div className="rise mt-10 flex flex-wrap gap-x-7 gap-y-3" style={{ animationDelay: ".38s" }}>
          {[[Activity, "Batting Analysis"], [Target, "Bowling Analysis"], [FileBarChart2, "Detailed Performance Reports"]].map(([I, t]) => (
            <span key={t} className="flex items-center gap-2 text-sm text-[#C9D1C9]"><I size={16} className="text-[#B7FF3C]" />{t}</span>
          ))}
        </div>
      </div>
      <div className="flex justify-center lg:justify-end rise" style={{ animationDelay: ".3s" }}><DashboardPreview /></div>
    </div>
  </section>
);
